'use client';
import { createTheme, type ThemeOptions } from '@mui/material/styles';
import siteConfig from '@/config/site';

/**
 * Thème LC Coiffure — dérivé du brief (docs/site-design.md).
 *
 * Le salon refait à neuf vu en plein jour : crème chaude, bois clair (sable),
 * un soupçon de sauge. La profondeur vient des changements de surface, pas des
 * ombres. Les couleurs de marque viennent de config/site.ts → brand.
 *
 * Typo : Fraunces (display, 300–400, tracking -0.01em, SOFT élevé) pour les
 * titres, Mulish (400/600, ~16 px, interligne 1.65) pour le texte — chargées
 * via next/font dans app/layout.tsx, exposées en variables CSS.
 */

const CREAM = '#FAF6EF';
const SAND = '#EFE7D9';
const INK = '#2E2A24';
const HAIRLINE = 'rgba(46,42,36,.12)';

const base: ThemeOptions = {
  palette: {
    mode: siteConfig.brand.mode,
    primary: {
      main: siteConfig.brand.primary, // terracotta — fil de mèche + CTA
      dark: '#9A5233', // un cran plus foncé au survol (brief § Motion)
      contrastText: CREAM,
    },
    secondary: { main: siteConfig.brand.secondary }, // sauge — bandeau éco uniquement
    background: { default: CREAM, paper: SAND },
    text: { primary: INK, secondary: 'rgba(46,42,36,.72)' },
    divider: HAIRLINE,
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: 'var(--font-mulish), system-ui, -apple-system, sans-serif',
    fontSize: 16,
    h1: {
      fontFamily: 'var(--font-fraunces), Georgia, serif',
      fontWeight: 300,
      fontSize: '2.75rem', // 44 px — monte à 56 px en desktop via sx
      lineHeight: 1.12,
      letterSpacing: '-0.01em',
      fontVariationSettings: '"SOFT" 100, "WONK" 0',
    },
    h2: {
      fontFamily: 'var(--font-fraunces), Georgia, serif',
      fontWeight: 400,
      fontSize: '2rem', // 32 px
      lineHeight: 1.15,
      letterSpacing: '-0.01em',
      fontVariationSettings: '"SOFT" 100, "WONK" 0',
    },
    h3: {
      fontFamily: 'var(--font-fraunces), Georgia, serif',
      fontWeight: 400,
      fontSize: '1.375rem', // 22 px
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.65 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: CREAM },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 999, // pilules
          // Interaction éditoriale : un cran plus foncé, aucune translation.
          transition: 'background-color 200ms ease, color 200ms ease, border-color 200ms ease',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${HAIRLINE}`,
          boxShadow: '0 1px 2px rgba(46,42,36,.06)',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textUnderlineOffset: 3,
          '&:hover': { color: siteConfig.brand.primary }, // souligné terracotta
        },
      },
    },
  },
};

export const theme = createTheme(base);
export default theme;
